import { SortType } from "../SortType";

/** Entity shape returned by `GET /v1/catalog/categories` list endpoint. */
export interface CategoryListItem {
  id: number;
  name: string;
  code: string | null;
  priority: number;
  display: number;
  active: number;
  seo_title: string | null;
  seo_description: string | null;
  has_expiry: number | null;
  selected_image_url: string | null;
  unselected_image_url: string | null;
  created_date: string | null;
  modified_date: string | null;
}

/** @deprecated Prefer CategoryOptionMapping via GET/PUT `/:id/options`. */
export interface CategorySkuVariantValue {
  id: number;
  name: string;
  sku_code: string;
  display_order: number;
  active: number;
}

/** @deprecated Prefer CategoryOptionMapping via GET/PUT `/:id/options`. */
export interface CategorySkuVariantMapping {
  id: number;
  variant_category_id: number;
  variant_category_name: string;
  variant_category_description: string | null;
  sku_sequence: number;
  display_order: number;
  is_required: number;
  active: number;
  values: CategorySkuVariantValue[];
}

export interface CategoryDetail extends CategoryListItem {
  image_url: string | null;
  size_chart_url: string | null;
  markup: number | null;
  /** @deprecated Prefer options from GET `/:id/options`. */
  sku_variants?: CategorySkuVariantMapping[];
}

/** Catalog option mapped onto a category (GET `/:id/options`). */
export interface CategoryOptionMapping {
  id: number;
  category_id: number;
  option_id: number;
  option_name: string;
  option_slug: string | null;
  option_display_type: string | null;
  display_order: number;
  is_required: number;
  active: number;
  created_by: number | null;
  updated_by: number | null;
  created_date: string | null;
  modified_date: string | null;
}

export interface CategoryOptionMappingInput {
  option_id: number;
  display_order: number;
  is_required: number;
  active?: number;
}

export interface PutCategoryOptionsBody {
  options: CategoryOptionMappingInput[];
}

/** @deprecated Prefer CategoryOptionMappingInput. */
export interface CategorySkuVariantInput {
  variant_category_id: number;
  sku_sequence: number;
  display_order: number;
  is_required: number;
  active?: number;
}

export interface CategoryCreateUpdatePayload {
  name: string;
  code: string;
  priority: number;
  display: number;
  active: number;
  seo_title?: string | null;
  seo_description?: string | null;
  has_expiry: number;
  selected_image_url?: string | null;
  unselected_image_url?: string | null;
}

/** Legacy shape still referenced by product dropdown APIs. */
export interface Category {
  id: number;
  name: string;
  priority?: number;
  display?: boolean | number;
  active?: boolean | number;
  desc?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  creator_name?: string | null;
  created_date?: string | null;
  modifier_name?: string | null;
  modified_date?: string | null;
  parent_id?: number | null;
  parent_name?: string | null;
  category_image_url?: string | null;
  code?: string | null;
}

export interface GetCategoriesParams {
  page_no: number;
  limit?: number;
  sort_by?: string;
  sort_type?: SortType;
  search?: string;
  active?: boolean;
  display?: boolean;
}

const SORT_BY_FIELDS = [
  "id",
  "name",
  "priority",
  "active",
  "display",
  "code",
] as const;
export type CategoryListSortBy = (typeof SORT_BY_FIELDS)[number];

export const CATEGORY_SORT_BY_OPTIONS: {
  value: CategoryListSortBy;
  label: string;
}[] = [
  { value: "id", label: "ID" },
  { value: "name", label: "Name" },
  { value: "code", label: "Code" },
  { value: "priority", label: "Priority" },
  { value: "display", label: "Display" },
  { value: "active", label: "Active" },
];

export function normalizeCategoryListSortBy(
  raw: string | null | undefined,
): CategoryListSortBy {
  if (raw && (SORT_BY_FIELDS as readonly string[]).includes(raw)) {
    return raw as CategoryListSortBy;
  }
  return "name";
}

export const CATEGORY_CODE_PATTERN = /^[A-Z0-9]+$/;

export function normalizeCategoryCode(raw: string): string {
  return raw.trim().toUpperCase();
}

export function validateCategoryCode(code: string): string | null {
  const normalized = normalizeCategoryCode(code);
  if (!normalized) return "Product code is required.";
  if (normalized.length > 10)
    return "Product code must be at most 10 characters.";
  if (!CATEGORY_CODE_PATTERN.test(normalized))
    return "Product code must contain only uppercase letters and numbers (e.g. POL, HDY).";
  return null;
}

export function validateCategoryOptionMappings(
  options: CategoryOptionMappingInput[],
): string | null {
  const optionIds = new Set<number>();
  const displayOrders = new Set<number>();

  for (let i = 0; i < options.length; i++) {
    const row = options[i];
    if (!row.option_id || row.option_id <= 0) {
      return `Option is required (row ${i + 1}).`;
    }
    if (optionIds.has(row.option_id)) {
      return `Duplicate option at row ${i + 1}.`;
    }
    optionIds.add(row.option_id);

    if (displayOrders.has(row.display_order)) {
      return `Duplicate display order at row ${i + 1}.`;
    }
    displayOrders.add(row.display_order);

    if (row.display_order < 0) {
      return `Display order must be ≥ 0 (row ${i + 1}).`;
    }
    if (row.is_required !== 0 && row.is_required !== 1) {
      return `Mandatory flag must be 0 or 1 (row ${i + 1}).`;
    }
    if (row.active != null && row.active !== 0 && row.active !== 1) {
      return `Active flag must be 0 or 1 (row ${i + 1}).`;
    }
  }
  return null;
}

export function validateCategoryPayload(
  payload: CategoryCreateUpdatePayload,
): string | null {
  if (!payload.name?.trim()) return "Name is required.";
  const codeErr = validateCategoryCode(payload.code);
  if (codeErr) return codeErr;
  if (!Number.isInteger(payload.priority) || payload.priority < 0) {
    return "Priority must be an integer ≥ 0.";
  }
  for (const flag of ["display", "active", "has_expiry"] as const) {
    const v = payload[flag];
    if (v !== 0 && v !== 1) return `${flag} must be 0 or 1.`;
  }
  return null;
}

export function asFlag(value: boolean | number | null | undefined): number {
  if (value === 1 || value === true) return 1;
  return 0;
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "Something went wrong",
): string {
  const ax = error as {
    response?: { data?: { message?: string }; status?: number };
    message?: string;
  };
  return ax?.response?.data?.message || ax?.message || fallback;
}
